/** How the rail sorts a business's rooms: kept at the top, active this week, and the rest behind All rooms. */
import type { HostRoomRow, TalkRoomKind } from "../../shared/talk";
import { dayNumber, formatAt } from "../lib/zones";

const WEEK = 7 * 24 * 3600;

export const roomNew = (r: HostRoomRow): number => r.everyoneNew + r.circles.reduce((n, c) => n + c.newCount, 0);
export const roomLive = (r: HostRoomRow): boolean => r.circles.some((c) => c.live);
export const kindLine = (r: HostRoomRow): string => `${r.kind} · ${r.people.toLocaleString("en-US")} ${r.people === 1 ? "person" : "people"}`;

const newestTalk = (a: HostRoomRow, b: HostRoomRow) => (b.lastTalkAt ?? 0) - (a.lastTalkAt ?? 0);

export interface RailSections {
  kept: HostRoomRow[];
  active: HostRoomRow[];
}

/**
 * Kept rooms first. A room that is not kept is active when something is new in it, a circle is live, or anything was
 * said or announced in the last seven days. Newest talk first in both.
 */
export function railSections(rooms: readonly HostRoomRow[], nowSec: number = Math.floor(Date.now() / 1000)): RailSections {
  const active = (r: HostRoomRow) => roomNew(r) > 0 || roomLive(r) || (r.lastTalkAt !== null && nowSec - r.lastTalkAt <= WEEK);
  return {
    kept: rooms.filter((r) => r.kept).toSorted(newestTalk),
    active: rooms.filter((r) => !r.kept && active(r)).toSorted(newestTalk),
  };
}

/** The room Conversations opens when none was chosen: the first kept, else the first active, else any. */
export function firstRoom(rooms: readonly HostRoomRow[]): HostRoomRow | null {
  const s = railSections(rooms);
  return s.kept[0] ?? s.active[0] ?? rooms[0] ?? null;
}

export interface AllRoomsView {
  kinds: { kind: TalkRoomKind; count: number }[];
  shown: HostRoomRow[];
}

const KINDS: TalkRoomKind[] = ["Weekly", "Plays any time", "One-time"];

/** The All rooms list: filtered by name and kind, in latest talk or A to Z order. */
export function allRoomsView(rooms: readonly HostRoomRow[], find: string, kind: TalkRoomKind | "all", order: "recent" | "az"): AllRoomsView {
  const word = find.trim().toLowerCase();
  const named = word ? rooms.filter((r) => r.title.toLowerCase().includes(word)) : rooms;
  const kinds = KINDS.map((k) => ({ kind: k, count: named.filter((r) => r.kind === k).length })).filter((k) => k.count > 0);
  const ofKind = kind === "all" ? named : named.filter((r) => r.kind === kind);
  return { kinds, shown: order === "az" ? ofKind.toSorted((x, y) => x.title.localeCompare(y.title)) : ofKind.toSorted(newestTalk) };
}

/** When something happened, in a rail row's words: today, yesterday, the weekday inside a week, then "22 Oct". */
export function whenWords(at: number, nowMs: number = Date.now()): string {
  const days = dayNumber(nowMs) - dayNumber(at * 1000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return formatAt(at * 1000, { weekday: "short" });
  return `${formatAt(at * 1000, { day: "numeric" })} ${formatAt(at * 1000, { month: "short" })}`;
}

/** When a room last talked; a room nobody has talked in says so. */
export const lastTalkWords = (r: HostRoomRow, nowMs: number = Date.now()): string => (r.lastTalkAt === null ? "No talk yet" : whenWords(r.lastTalkAt, nowMs));
